import { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const API = "https://happyhorizon.co.in/api";

export default function ImageManager() {
  const [images, setImages] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const inputRef = useRef();

  /* ===== FETCH ALL IMAGES ON LOAD ===== */
  const fetchImages = async () => {
    try {
      const res = await fetch(`${API}/get_images.php`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setImages(data);
      } else if (data.success && data.images) {
        setImages(data.images);
      }
    } catch (err) {
      console.error("Error fetching images:", err);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  /* ===== SHOW NOTIFICATION ===== */
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  /* ===== UPLOAD MULTIPLE ===== */
  const upload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadedCount(0);

    let successCount = 0;
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fd = new FormData();
      fd.append("image", file);

      try {
        const res = await fetch(`${API}/upload_image.php`, {
          method: "POST",
          body: fd
        });

        const data = await res.json();
        if (data.success) {
          successCount++;
        }
      } catch (err) {
        console.error(`Upload error for ${file.name}:`, err);
      }

      // Update progress
      const progress = Math.round(((i + 1) / totalFiles) * 100);
      setUploadProgress(progress);
      setUploadedCount(i + 1);
    }

    // Complete
    setFiles([]);
    setUploadProgress(100);
    setTimeout(() => {
      setUploading(false);
      setUploadProgress(0);
      setUploadedCount(0);
      fetchImages();
      showNotification(
        successCount === totalFiles
          ? `All ${totalFiles} images uploaded successfully!`
          : `${successCount} of ${totalFiles} images uploaded. Some failed.`,
        successCount === totalFiles ? "success" : "warning"
      );
    }, 500);
  };

  /* ===== DELETE ===== */
  const deleteImage = async (id) => {
    setDeletingId(id);

    try {
      const res = await fetch(`${API}/delete_image.php?id=${id}`);
      const data = await res.json();

      // Remove from UI with animation
      setImages(images.filter(img => img.id !== id));
      setDeletingId(null);
      showNotification("Image deleted successfully!", "success");
    } catch (err) {
      console.error("Delete error:", err);
      setDeletingId(null);
      showNotification("Delete failed. Please try again.", "danger");
    }
  };

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div className="container-lg">
          <h1 style={styles.pageTitle}>
            🎨 Gallery Manager
          </h1>
          <p style={styles.pageSubtitle}>Upload and manage your beautiful moments</p>
        </div>
      </div>

      <div className="container-lg" style={styles.mainContent}>
        {/* NOTIFICATION */}
        {notification && (
          <div 
            className={`alert alert-${notification.type} alert-dismissible fade show mb-4`}
            role="alert"
            style={{ animation: "slideDown 0.3s ease" }}
          >
            <i className={`bi bi-${notification.type === "success" ? "check-circle" : "exclamation-circle"}`}></i>
            {" "}{notification.message}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setNotification(null)}
            ></button>
          </div>
        )}
        {/* UPLOAD SECTION */}
        <div className="row mb-5">
          <div className="col-lg-5">
            <div className="card border-0" style={styles.uploadCard}>
              <div className="card-body p-4">
                <h3 className="card-title mb-3">
                  ☁️ Upload Image
                </h3>
                <p className="text-muted mb-4">Drag and drop or click to select an image</p>

                <div
                  style={styles.dropZone}
                  onClick={() => inputRef.current.click()}
                  onDragOver={e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = "#0d6efd";
                    e.currentTarget.style.background = "rgba(13, 110, 253, 0.05)";
                  }}
                  onDragLeave={e => {
                    e.currentTarget.style.borderColor = "#dee2e6";
                    e.currentTarget.style.background = "#fff";
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
                    if (droppedFiles.length > 0) {
                      setFiles(droppedFiles);
                    }
                  }}
                >
                  <div style={styles.dropContent}>
                    <div style={{...styles.dropIcon, fontSize: "3rem"}}>🖼️</div>
                    <p style={styles.dropText}>
                      {files.length > 0 ? (
                        <>
                          <strong>{files.length} image(s) selected</strong>
                          <br />
                          <small className="text-success">Ready to upload</small>
                        </>
                      ) : (
                        <>
                          <strong>Click or drag images here</strong>
                          <br />
                          <small>Supported: JPG, PNG, GIF (Multiple OK)</small>
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={e => setFiles(Array.from(e.target.files))}
                />

                {uploading && (
                  <>
                    <div className="progress mt-3" style={{ height: "6px" }}>
                      <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <small className="text-muted mt-2 d-block">
                      Uploading {uploadedCount} of {files.length}...
                    </small>
                  </>
                )}

                <button
                  className="btn btn-primary w-100 mt-4"
                  onClick={upload}
                  disabled={files.length === 0 || uploading}
                  style={styles.uploadBtn}
                >
                  📤
                  {uploading 
                    ? ` Uploading... (${uploadedCount}/${files.length})` 
                    : files.length > 0 
                      ? ` Upload ${files.length} Image${files.length > 1 ? "s" : ""}`
                      : " Select Images"
                  }
                </button>

                {files.length > 0 && (
                  <button
                    className="btn btn-outline-secondary w-100 mt-2"
                    onClick={() => setFiles([])}
                  >
                    Clear Selection
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* STATS */}
          <div className="col-lg-7">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="card border-0 text-center" style={styles.statCard}>
                  <div className="card-body">
                    <div style={{...styles.statIcon, fontSize: "2.5rem"}}>🖼️</div>
                    <h2 className="mt-3">{images.length}</h2>
                    <p className="text-muted">Total Images</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 text-center" style={styles.statCard}>
                  <div className="card-body">
                    <div style={{...styles.statIcon, fontSize: "2.5rem"}}>✅</div>
                    <h2 className="mt-3">Ready</h2>
                    <p className="text-muted">Status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* GALLERY */}
        <h2 style={styles.gallerySectionTitle}>
          📚 Your Gallery ({images.length})
        </h2>

        {images.length === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: "3rem", color: "#ccc" }}>📭</div>
            <p className="text-muted mt-3">No images yet. Upload your first image!</p>
          </div>
        ) : (
          <div className="row g-4">
            {images.map(img => (
              <div 
                key={img.id} 
                className="col-lg-3 col-md-4 col-sm-6"
                style={{
                  animation: deletingId === img.id ? "slideOut 0.3s ease forwards" : "slideIn 0.3s ease",
                  opacity: deletingId === img.id ? 0 : 1
                }}
              >
                <div className="card border-0 overflow-hidden" style={{...styles.imageCard, position: "relative"}}>
                  <div 
                    style={styles.imageWrapper}
                    onClick={() => setSelectedImageId(selectedImageId === img.id ? null : img.id)}
                  >
                    <img
                      src={`https://happyhorizon.co.in/${img.url}`}
                      alt="Gallery"
                      style={styles.cardImage}
                    />
                    <div 
                      style={{
                        ...styles.imageOverlay,
                        opacity: selectedImageId === img.id ? 1 : 0,
                        background: selectedImageId === img.id ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0)"
                      }}
                    >
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteImage(img.id);
                        }}
                        disabled={deletingId === img.id}
                        style={{
                          transition: "all 0.3s ease",
                          opacity: deletingId === img.id ? 0.5 : 1
                        }}
                      >
                        {deletingId === img.id ? (
                          <>
                            <span 
                              className="spinner-border spinner-border-sm me-2" 
                              role="status" 
                              aria-hidden="true"
                              style={{ width: "12px", height: "12px" }}
                            ></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            🗑️ Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <small className="text-muted">ID: {img.id}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== STYLES ===== */
const styles = {
  container: {
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    minHeight: "100vh",
    paddingBottom: 60
  },

  header: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: "60px 20px 40px",
    marginBottom: 40,
    borderRadius: "0 0 30px 30px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)"
  },

  pageTitle: {
    fontSize: "2.5rem",
    fontWeight: 800,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },

  pageSubtitle: {
    fontSize: "1.1rem",
    opacity: 0.9,
    marginBottom: 0
  },

  mainContent: {
    paddingLeft: 20,
    paddingRight: 20
  },

  uploadCard: {
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
    borderRadius: "20px",
    transition: "transform 0.3s ease, box-shadow 0.3s ease"
  },

  dropZone: {
    border: "2px dashed #dee2e6",
    borderRadius: "15px",
    padding: "40px 20px",
    textAlign: "center",
    cursor: "pointer",
    background: "#fff",
    transition: "all 0.3s ease"
  },

  dropContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },

  dropIcon: {
    fontSize: "3rem",
    color: "#667eea",
    marginBottom: 10
  },

  dropText: {
    margin: 0,
    color: "#333"
  },

  uploadBtn: {
    padding: "12px 24px",
    fontSize: "1rem",
    fontWeight: 600,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: "10px",
    transition: "all 0.3s ease",
    boxShadow: "0 5px 15px rgba(102, 126, 234, 0.4)"
  },

  statCard: {
    borderRadius: "15px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
    background: "#fff",
    transition: "transform 0.3s ease"
  },

  statIcon: {
    fontSize: "2.5rem",
    color: "#667eea"
  },

  gallerySectionTitle: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: 30,
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },

  imageCard: {
    borderRadius: "15px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    transition: "transform 0.3s ease, box-shadow 0.3s ease"
  },

  imageWrapper: {
    position: "relative",
    overflow: "hidden",
    paddingBottom: "100%",
    background: "#f0f0f0"
  },

  cardImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.3s ease"
  },

  imageOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0, 0, 0, 0)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "all 0.3s ease"
  }
};

// Add animations via inline style tag
if (typeof document !== "undefined" && !document.getElementById("upload-animations")) {
  const style = document.createElement("style");
  style.id = "upload-animations";
  style.textContent = `
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideOut {
      from {
        opacity: 1;
        transform: translateX(0) scale(1);
      }
      to {
        opacity: 0;
        transform: translateX(-100%) scale(0.8);
      }
    }
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .card:hover {
      transform: translateY(-8px);
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2) !important;
    }
    
    .card:hover .imageOverlay {
      opacity: 1;
      background: rgba(0, 0, 0, 0.5) !important;
    }
    
    .card:hover img {
      transform: scale(1.05);
    }
  `;
  document.head.appendChild(style);
}
